import React, { useState, useMemo, useCallback, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { MultiSelect } from "@/components/MultiSelectUsers";
import { Edit, X, FileText } from "lucide-react";
import { API, fmtDate } from "@/App";
import api from "@/api/axios";

// --- Constants ---
const TRANSMITTAL_TYPES = ["Drawing", "Package", "Other"];
const INITIAL_FORM_STATE = {
    number: "",
    title: "",
    type: "Drawing",
    version: "",
    revision: "",
    date: "",
    files: "",
    recipients: [],
    recipientEmails: [],
    notes: "",
};

// --- Helper Functions ---
const formatDateForInput = (dateStr) => {
    if (!dateStr) return "";
    try {
        return new Date(dateStr).toISOString().split("T")[0];
    } catch (e) {
        return "";
    }
};

// --- Reusable Sub-Components (kept in the same file) ---

// 1. Reusable Form for Add/Edit
const TransmittalForm = ({ formData, onFormChange, onSubmit, recipientOptions, isEditing = false }) => {
    const handleChange = (field, value) => {
        onFormChange(prev => ({ ...prev, [field]: value }));
    };

    return (
        <form onSubmit={onSubmit} className="form-grid">
            {/* Input fields */}
            <div>
                <Label>Number</Label>
                <Input value={formData.number} onChange={(e) => handleChange('number', e.target.value)} />
            </div>
            <div>
                <Label>Title</Label>
                <Input value={formData.title} onChange={(e) => handleChange('title', e.target.value)} />
            </div>
            <div>
                <Label>Type</Label>
                <Select disabled={isEditing} value={formData.type} onValueChange={(v) => handleChange('type', v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                        {TRANSMITTAL_TYPES.map(type => <SelectItem key={type} value={type}>{type}</SelectItem>)}
                    </SelectContent>
                </Select>
            </div>
            <div>
                <Label>Version</Label>
                <Input value={formData.version} onChange={(e) => handleChange('version', e.target.value)} />
            </div>
            <div>
                <Label>Revision</Label>
                <Input value={formData.revision} onChange={(e) => handleChange('revision', e.target.value)} />
            </div>
            <div>
                <Label>Date</Label>
                <Input type="date" value={formData.date} onChange={(e) => handleChange('date', e.target.value)} />
            </div>
            <div className="col-span-2">
                <Label>Files (comma-separated URLs)</Label>
                <Input value={formData.files} onChange={(e) => handleChange('files', e.target.value)} />
            </div>
            <div className="col-span-2">
                <Label>Recipients</Label>
                <MultiSelect
                    options={recipientOptions}
                    value={formData.recipients}
                    onChange={(ids) => handleChange('recipients', ids)}
                    placeholder="Select recipients"
                />
            </div>
            <div className="col-span-2">
                <Label>Recipient Email IDs</Label>

                {/* Chips Display */}
                <div className="flex gap-1 flex-wrap mb-1">
                    {formData.recipientEmails?.map(email => (
                        <span
                            key={email}
                            className="bg-green-200 text-green-900 px-2 py-1 rounded-full text-xs cursor-pointer"
                            onClick={() => {
                                onFormChange(prev => ({
                                    ...prev,
                                    recipientEmails: prev.recipientEmails.filter(e => e !== email)
                                }));
                            }}
                        >
        {email} ✕
      </span>
                    ))}
                </div>

                {/* Input with inline logic */}
                <div className="relative">
                    <Input
                        placeholder="Type email and press comma, space or Enter"
                        value={formData.recipientEmailsRaw || ""}
                        onChange={(e) => {
                            const raw = e.target.value;
                            onFormChange(prev => ({ ...prev, recipientEmailsRaw: raw }));
                        }}
                        onKeyDown={(e) => {
                            if (["Enter", "Tab", ",", " "].includes(e.key)) {
                                e.preventDefault();
                                const email = (formData.recipientEmailsRaw || "")
                                    .trim()
                                    .replace(/,$/, "");

                                if (email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
                                    onFormChange(prev => ({
                                        ...prev,
                                        recipientEmails: [...(prev.recipientEmails || []), email],
                                        recipientEmailsRaw: ""
                                    }));
                                }
                            }
                        }}
                        className={(formData.recipientEmailsRaw?.trim() &&
                            !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.recipientEmailsRaw.trim()))
                            ? "border-red-500"
                            : ""}
                    />

                    {/* Valid count */}
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-600 whitespace-nowrap">
      {(formData.recipientEmails?.length || 0)} ✅
    </span>
                </div>

                <p className="text-xs text-gray-500 mt-1">
                    Example: test@gmail.com, someone@domain.com
                </p>
            </div>


            <div className="col-span-2">
                <Label>Notes</Label>
                <Input value={formData.notes} onChange={(e) => handleChange('notes', e.target.value)} />
            </div>
            <div className="col-span-2 flex justify-end">
                <Button type="submit">{isEditing ? "Update Transmittal" : "Add Transmittal"}</Button>
            </div>
        </form>
    );
};

// 2. Display Card for each Transmittal
const TransmittalCard = React.memo(({ transmittal, onEdit, users }) => (
    <div className="bg-white rounded-xl shadow-lg border border-gray-200 hover:shadow-xl transition-all duration-300 p-6 relative group">
        <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity">
            <Button variant="ghost" size="sm" onClick={() => onEdit(transmittal)} className="h-8 w-8 p-0">
                <Edit className="w-4 h-4" />
            </Button>
        </div>
        <div className="flex justify-between items-start mb-4">
            <div className="flex items-center gap-2">
                <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">#{transmittal.number || "—"}</span>
                <span className="bg-gray-100 text-gray-700 px-2 py-1 rounded-md text-xs font-medium">{transmittal.type || "—"}</span>
            </div>
            <div className="text-right">
                <div className="text-xs text-gray-500">Version</div>
                <div className="font-semibold">{transmittal.version || "—"}</div>
            </div>
        </div>
        <h3 className="font-semibold text-lg mb-3 line-clamp-2 text-gray-900 pr-8">{transmittal.title || "Untitled"}</h3>
        <div className="grid grid-cols-2 gap-3 mb-4 text-sm">
            <div>
                <div className="text-gray-500 text-xs">Revision</div>
                <div className="font-medium">{transmittal.revision || "—"}</div>
            </div>
            <div>
                <div className="text-gray-500 text-xs">Date</div>
                <div className="font-medium">{fmtDate(transmittal.date)}</div>
            </div>
        </div>
        <div className="mb-4">
            <div className="text-gray-500 text-xs mb-2">Files</div>
            {transmittal.files?.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                    {transmittal.files.map((fileUrl, i) => (
                        <a key={i} href={fileUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 bg-blue-50 text-blue-700 px-3 py-1 rounded-lg text-sm hover:bg-blue-100 transition-colors">
                            <FileText className="w-3.5 h-3.5" /> File {i + 1}
                        </a>
                    ))}
                </div>
            ) : <span className="text-gray-400 text-sm">—</span>}
        </div>
        {/* Recipients Section */}
        <div className="mb-4">
            <div className="text-gray-500 text-xs mb-2">Recipients</div>

            {/* By User ID */}
            {transmittal.recipients?.length > 0 && (
                <div className="flex flex-wrap gap-1 mb-1">
                    {transmittal.recipients.map((r, i) => (
                        <span key={i} className="inline-block bg-green-50 text-green-700 px-2 py-1 rounded-md text-xs">
                    {users.find(u => u.id === r)?.name || r}
                </span>
                    ))}
                </div>
            )}

            {/* ✅ Email Recipients */}
            {transmittal.recipientEmails?.length > 0 && (
                <div className="flex flex-wrap gap-1">
                    {transmittal.recipientEmails.map((email, i) => (
                        <span key={i} className="inline-block bg-yellow-50 text-yellow-700 px-2 py-1 rounded-md text-xs">
                    {email}
                </span>
                    ))}
                </div>
            )}

            {(!transmittal.recipients?.length && !transmittal.recipientEmails?.length) && (
                <span className="text-gray-400 text-sm">—</span>
            )}
        </div>

        {/* Notes Section */}
        {transmittal.notes && (
            <div className="mt-4 pt-4 border-t border-gray-100">
                <div className="text-gray-500 text-xs mb-2">Notes</div>
                <p className="text-sm text-gray-700 line-clamp-3">{transmittal.notes}</p>
            </div>
        )}
    </div>
));

// 3. Modal for Editing
const EditModal = ({ transmittal, onClose, onUpdate, recipientOptions }) => {
    const [editForm, setEditForm] = useState({ ...INITIAL_FORM_STATE });

    useEffect(() => {
        if (transmittal) {
            transmittal.version = Number(transmittal.version || 0) + 1;
            setEditForm({
                ...transmittal,
                date: formatDateForInput(transmittal.date),
                recipientEmails: transmittal.recipientEmails || [],
                files: Array.isArray(transmittal.files) ? transmittal.files.join(", ") : transmittal.files || "",
            });
        }
    }, [transmittal]);

    if (!transmittal) return null;


    const handleSubmit = (e) => {
        e.preventDefault();
        onUpdate(transmittal.id, editForm);
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <Card className="max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                <CardHeader>
                    <div className="flex justify-between items-center">
                        <CardTitle>Edit Transmittal</CardTitle>
                        <Button variant="ghost" size="sm" onClick={onClose}><X className="w-4 h-4" /></Button>
                    </div>
                </CardHeader>
                <CardContent>
                    <TransmittalForm
                        formData={editForm}
                        onFormChange={setEditForm}
                        onSubmit={handleSubmit}
                        recipientOptions={recipientOptions}
                        isEditing={true}
                    />
                </CardContent>
            </Card>
        </div>
    );
};

// --- Main Component ---
const Transmittals = ({ transmittals = [], projectID, team = [], users = [], user, onDataChange }) => {
    const [trForm, setTrForm] = useState(INITIAL_FORM_STATE);
    const [editingTransmittal, setEditingTransmittal] = useState(null);

    // Memoize recipient options to prevent recalculation on every render
    const recipientOptions = useMemo(() =>
        team.map((t) => ({
            value: t.user_id,
            label: users.find((u) => u.id === t.user_id)?.name || t.user_id,
        })), [team, users]
    );

    // Centralized payload creation
    const preparePayload = (formData) => ({
        project_id: projectID,
        number: formData.number || null,
        title: formData.title || null,
        type: formData.type,
        version: formData.version || null,
        revision: formData.revision || null,
        date: formData.date || null,
        files: (formData.files || "").split(",").map(s => s.trim()).filter(Boolean),
        recipients: Array.isArray(formData.recipients) ? formData.recipients : [],

        recipientEmails: Array.isArray(formData.recipientEmails)
            ? formData.recipientEmails
            : [],

        notes: formData.notes || null,
        sent_by: user?.name || user?.id,
    });

    const handleAdd = useCallback(async (e) => {
        e.preventDefault();
        const payload = preparePayload(trForm);
        await api.post(`${API}/transmittals/${projectID}`, payload);
        setTrForm(INITIAL_FORM_STATE); // Reset form
        onDataChange(); // Notify parent to refetch data
    }, [trForm, projectID, user, onDataChange]);

    const handleUpdate = useCallback(async (id, formData) => {
        const payload = preparePayload(formData);
        await api.patch(`${API}/transmittals/${id}`, payload);
        setEditingTransmittal(null); // Close modal
        onDataChange(); // Notify parent to refetch data
    }, [onDataChange]);

    const handleEditClick = useCallback((transmittal) => {
        setEditingTransmittal(transmittal);
    }, []);

    const handleCloseModal = useCallback(() => {
        setEditingTransmittal(null);
    }, []);

    return (
        <>
            <Card className="mt-4">
                <CardHeader>
                    <CardTitle>Add New Transmittal</CardTitle>
                </CardHeader>
                <CardContent>
                    <TransmittalForm
                        formData={trForm}
                        onFormChange={setTrForm}
                        onSubmit={handleAdd}
                        recipientOptions={recipientOptions}
                    />
                </CardContent>
            </Card>

            <div className="mt-6">
                <h3 className="text-xl font-semibold mb-4">Existing Transmittals</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {transmittals.map((t) => (
                        <TransmittalCard key={t.id} transmittal={t} onEdit={handleEditClick} users={users} />
                    ))}

                    {transmittals.length === 0 && (
                        <div className="col-span-full text-center py-12 bg-gray-50 rounded-lg">
                            <p className="text-gray-500">No transmittals found. Use the form above to create one.</p>
                        </div>
                    )}
                </div>
            </div>

            <EditModal
                transmittal={editingTransmittal}
                onClose={handleCloseModal}
                onUpdate={handleUpdate}
                recipientOptions={recipientOptions}
            />
        </>
    );
};

export default Transmittals;